import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { AccountTitleFormSchema, AccountTitlesModel } from '../models';

export const useAccountTitles = <T = z.infer<typeof AccountTitlesModel>>({
  select,
  params,
  enabled,
}: {
  select?: (data: z.infer<typeof AccountTitlesModel>) => T;
  params?: { referenceFiscalYear?: string; showMigrated?: true };
  enabled?: boolean;
} = {}) => {
  const { showMigrated, ...restParams } = params ?? {};
  return useQuery({
    queryKey: ['accountTitles', params],
    queryFn: async () => {
      const data = await axiosGet('/api/account-titles/', {
        ...restParams,
        ...(!showMigrated && { isMigrated: false }),
      });
      return AccountTitlesModel.parse(data);
    },
    select,
    enabled,
  });
};

export const useAccountTitleOptions = ({
  accountingId,
  params,
  enabled,
}: {
  accountingId?: string;
  params?: { referenceFiscalYear?: string; showMigrated?: true };
  enabled?: boolean;
} = {}) => {
  return useAccountTitles({
    params,
    select: (data) =>
      data.flatMap((d) =>
        !accountingId || d.accountingId === accountingId
          ? {
              value: d.id,
              label: d.name,
            }
          : [],
      ),
    enabled,
  });
};

type FormValues = z.infer<typeof AccountTitleFormSchema>;

export const useCreateAccountTitle = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { period, ...payload } = AccountTitleFormSchema.parse(values);
      const res = await axiosPost('/api/account-titles/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });

      await invalidate(['accountTitles']);

      return res;
    },
  });
};

export const useUpdateAccountTitle = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, period, ...payload } = AccountTitleFormSchema.extend({
        id: z.string().uuid(),
      }).parse(values);
      const res = await axiosPatch(`/api/account-titles/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });

      await invalidate(['accountTitles']);

      return res;
    },
  });
};

export const useDeleteAccountTitle = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      await axiosDelete(`/api/account-titles/${id}/`);
      await invalidate(['accountTitles']);
    },
  });
};
